/**
 * Generate Intelligence Batch Panel
 * ---------------------------------------------------------------
 * Behavioral signal expansion engine. Operator-grade controls
 * over scenario count, formats, escalation paths, behavioral
 * intensity and archetype focus. Each click generates a full
 * matrix of assets, persists telemetry, and broadcasts to the
 * downstream queues.
 *
 * Tactical UI only. Same forensic dark/cyan/amber palette.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, Radar, Activity, AlertTriangle, Crosshair, Layers,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { ARCHETYPES, type ArchetypeKey } from "@/lib/simulationEngine";
import { SCENARIOS, pickScenario } from "@/lib/scenarioLibrary";
import {
  generateSimulationContent,
  type ContentFormat,
} from "@/lib/contentGenerator";
import {
  registerGeneratedContent,
  emitContentAssetsChanged,
} from "@/lib/contentTelemetry";
import { scoreAndPersist, type PredictionFeatures } from "@/lib/predictionEngine";
import {
  fetchActiveBiases,
  buildArchetypeWeights,
  weightedShuffle,
  biasPreferences,
  summarizeBiases,
  type ActiveBias,
} from "@/lib/adaptiveBiases";

// ---------- Static option sets ----------

const FORMATS: { key: ContentFormat; label: string }[] = [
  { key: "replay", label: "Replay" },
  { key: "spiral", label: "Spiral" },
  { key: "pov", label: "POV" },
];

const ESCALATIONS = [
  { key: "emotional", label: "Emotional" },
  { key: "defensive", label: "Defensive" },
  { key: "guilt", label: "Guilt" },
  { key: "withdrawal", label: "Withdrawal" },
] as const;

type EscalationKey = (typeof ESCALATIONS)[number]["key"];

const INTENSITY_LABELS = ["Low", "Medium", "High"] as const;
type IntensityLabel = (typeof INTENSITY_LABELS)[number];

const ARCHETYPE_KEYS = Object.keys(ARCHETYPES) as ArchetypeKey[];

// Pipeline stage labels (rotating ops log during generation)
const PIPELINE_STAGES = [
  "Generating behavioral matrix...",
  "Creating escalation variants...",
  "Registering telemetry...",
  "Initializing signal layer...",
  "Indexing batch metadata...",
];

// ---------- Component ----------

export default function GenerateIntelligenceBatchPanel() {
  const [scenarioCount, setScenarioCount] = useState(20);
  const [selectedFormats, setSelectedFormats] = useState<Set<ContentFormat>>(
    new Set(FORMATS.map((f) => f.key))
  );
  const [selectedEscalations, setSelectedEscalations] = useState<Set<EscalationKey>>(
    new Set(ESCALATIONS.map((e) => e.key))
  );
  const [intensityIdx, setIntensityIdx] = useState(1); // medium
  const [allArchetypes, setAllArchetypes] = useState(true);
  const [selectedArchetypes, setSelectedArchetypes] = useState<Set<ArchetypeKey>>(
    new Set(ARCHETYPE_KEYS)
  );

  const [isGenerating, setIsGenerating] = useState(false);
  const [pipelineStage, setPipelineStage] = useState<string>("");
  const [pipelineLog, setPipelineLog] = useState<string[]>([]);
  const [liveCounter, setLiveCounter] = useState(0);
  const [lastError, setLastError] = useState<string | null>(null);
  const [lastBatch, setLastBatch] = useState<{ count: number; ms: number } | null>(null);

  const stageTimerRef = useRef<number | null>(null);

  const intensity: IntensityLabel = INTENSITY_LABELS[intensityIdx];

  const archetypePool: ArchetypeKey[] = useMemo(() => {
    if (allArchetypes) return ARCHETYPE_KEYS;
    return ARCHETYPE_KEYS.filter((k) => selectedArchetypes.has(k));
  }, [allArchetypes, selectedArchetypes]);

  const projectedAssets =
    scenarioCount *
    Math.max(1, selectedFormats.size) *
    Math.max(1, selectedEscalations.size);

  const renderLoad =
    projectedAssets < 60 ? "Light" :
    projectedAssets < 200 ? "Medium" :
    projectedAssets < 500 ? "Heavy" : "Saturating";

  const estimatedMs = Math.round(projectedAssets * 75 + 800); // ~75ms/asset projection
  const coverage =
    archetypePool.length === ARCHETYPE_KEYS.length && selectedEscalations.size === 4
      ? "High"
      : archetypePool.length >= 4 || selectedEscalations.size >= 3
        ? "Medium"
        : "Narrow";

  // ---------- Toggles ----------

  const toggleFormat = (f: ContentFormat) => {
    setSelectedFormats((prev) => {
      const n = new Set(prev);
      n.has(f) ? n.delete(f) : n.add(f);
      return n;
    });
  };
  const toggleEscalation = (e: EscalationKey) => {
    setSelectedEscalations((prev) => {
      const n = new Set(prev);
      n.has(e) ? n.delete(e) : n.add(e);
      return n;
    });
  };
  const toggleArchetype = (a: ArchetypeKey) => {
    setSelectedArchetypes((prev) => {
      const n = new Set(prev);
      n.has(a) ? n.delete(a) : n.add(a);
      return n;
    });
  };

  // ---------- Pipeline log animation ----------

  useEffect(() => {
    if (!isGenerating) {
      if (stageTimerRef.current) {
        clearInterval(stageTimerRef.current);
        stageTimerRef.current = null;
      }
      return;
    }
    let i = 0;
    setPipelineStage(PIPELINE_STAGES[0]);
    stageTimerRef.current = window.setInterval(() => {
      i = (i + 1) % PIPELINE_STAGES.length;
      setPipelineStage(PIPELINE_STAGES[i]);
    }, 900);
    return () => {
      if (stageTimerRef.current) {
        clearInterval(stageTimerRef.current);
        stageTimerRef.current = null;
      }
    };
  }, [isGenerating]);

  const pushLog = (line: string) => {
    setPipelineLog((prev) => [`${new Date().toLocaleTimeString()}  ${line}`, ...prev].slice(0, 60));
  };

  // ---------- Generation ----------

  const handleGenerate = async () => {
    if (selectedFormats.size === 0) {
      setLastError("Select at least one format.");
      return;
    }
    if (selectedEscalations.size === 0) {
      setLastError("Select at least one escalation path.");
      return;
    }
    if (archetypePool.length === 0) {
      setLastError("Select at least one archetype focus.");
      return;
    }
    setLastError(null);
    setLastBatch(null);
    setLiveCounter(0);
    setPipelineLog([]);
    setIsGenerating(true);

    const batchId = (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`);
    const t0 = performance.now();

    const formats = Array.from(selectedFormats);
    const escalations = Array.from(selectedEscalations);
    const archetypeUsage: Record<string, number> = {};
    const allAssetIds: string[] = [];
    const predictionFeatures: PredictionFeatures[] = [];

    pushLog(`BATCH ${batchId.slice(0, 8)} — initializing.`);
    pushLog(`Pool: ${scenarioCount} scenarios · ${formats.length} formats · ${escalations.length} escalations.`);

    // Log batch start to content_events
    void supabase.from("content_events" as any).insert({
      content_asset_id: null,
      event_type: "intelligence_batch_started",
      metadata: {
        batch_id: batchId,
        scenario_count: scenarioCount,
        formats,
        escalation_paths: escalations,
        intensity,
        archetypes: archetypePool,
        projected_assets: projectedAssets,
      } as any,
    });

    try {
      // Fetch active intelligence biases — these are the persisted
      // directives committed by the Adaptive Learning Engine.
      const activeBiases: ActiveBias[] = await fetchActiveBiases();
      if (activeBiases.length > 0) {
        pushLog(`Adaptive layer engaged — ${summarizeBiases(activeBiases)}.`);
      } else {
        pushLog(`No active intelligence directives — neutral generation.`);
      }

      // Build the asset matrix.
      // Pick scenarios from the pool filtered by archetype focus,
      // then re-weighted by active archetype biases.
      let scenarioPool = SCENARIOS.filter((s) =>
        archetypePool.includes(s.archetype)
      );
      if (scenarioPool.length === 0) {
        throw new Error("No scenarios match the selected archetype focus.");
      }

      const archetypeWeights = buildArchetypeWeights(activeBiases, archetypePool);
      scenarioPool = weightedShuffle(scenarioPool, (s) => archetypeWeights[s.archetype] ?? 1);

      // Format / escalation re-ordering — preferred biases first
      const formatPrefs = biasPreferences(activeBiases, "format");
      const escalationPrefs = biasPreferences(activeBiases, "escalation");
      const orderedFormats = [...formats].sort((a, b) => {
        const aw = formatPrefs.prefer.indexOf(a) >= 0 ? -1 : formatPrefs.suppress.indexOf(a) >= 0 ? 1 : 0;
        const bw = formatPrefs.prefer.indexOf(b) >= 0 ? -1 : formatPrefs.suppress.indexOf(b) >= 0 ? 1 : 0;
        return aw - bw;
      });
      const orderedEscalations = [...escalations].sort((a, b) => {
        const aw = escalationPrefs.prefer.indexOf(a) >= 0 ? -1 : escalationPrefs.suppress.indexOf(a) >= 0 ? 1 : 0;
        const bw = escalationPrefs.prefer.indexOf(b) >= 0 ? -1 : escalationPrefs.suppress.indexOf(b) >= 0 ? 1 : 0;
        return aw - bw;
      });

      for (let i = 0; i < scenarioCount; i++) {
        const scenario = scenarioPool[i % scenarioPool.length] ?? pickScenario();
        archetypeUsage[scenario.archetype] = (archetypeUsage[scenario.archetype] ?? 0) + 1;

        for (const format of orderedFormats) {
          for (const escalation of orderedEscalations) {
            const content = generateSimulationContent({ format, scenario });
            const registered = await registerGeneratedContent(content, {
              platform: "internal",
              behavioralIntensity: intensity.toLowerCase() as "low" | "medium" | "high",
              escalationPath: escalation,
              scenarioSeed: scenario.id,
              generationBatchId: batchId,
            });
            allAssetIds.push(registered.assetId);
            predictionFeatures.push({
              assetId: registered.assetId,
              archetype: scenario.archetype,
              format,
              escalationPath: escalation,
              pacing: intensity.toLowerCase(),
              platform: "internal",
              hook: content.hook,
            });
            setLiveCounter((c) => c + 1);
          }
        }
        if (i % 5 === 0) {
          pushLog(`Indexed scenario ${i + 1}/${scenarioCount} — ${scenario.archetype}.`);
        }
      }

      const durationMs = Math.round(performance.now() - t0);
      pushLog(`Telemetry registered for ${allAssetIds.length} assets.`);

      // Predictive Viral Scoring — pre-render forecasting
      pushLog(`Forecasting behavioral outcomes…`);
      const preds = await scoreAndPersist(predictionFeatures);
      const avgVirality = preds.length
        ? Math.round(preds.reduce((s, p) => s + p.predictedViralityScore, 0) / preds.length)
        : 0;
      pushLog(`Forecast complete — avg virality ${avgVirality}/100 across ${preds.length} assets.`);

      pushLog(`Batch indexing complete — ${durationMs}ms.`);

      void supabase.from("content_events" as any).insert({
        content_asset_id: null,
        event_type: "intelligence_batch_completed",
        metadata: {
          batch_id: batchId,
          asset_count: allAssetIds.length,
          asset_ids: allAssetIds,
          scenario_count: scenarioCount,
          formats,
          escalation_paths: escalations,
          intensity,
          archetypes_used: archetypeUsage,
          duration_ms: durationMs,
          status: "completed",
        } as any,
      });

      setLastBatch({ count: allAssetIds.length, ms: durationMs });
      emitContentAssetsChanged({ batchId, assetIds: allAssetIds });
    } catch (err: any) {
      const msg = err?.message ?? "Pipeline interrupted";
      setLastError(msg);
      pushLog(`FAILED — ${msg}`);
      void supabase.from("content_events" as any).insert({
        content_asset_id: null,
        event_type: "intelligence_batch_failed",
        metadata: {
          batch_id: batchId,
          error: msg,
          duration_ms: Math.round(performance.now() - t0),
          asset_count: allAssetIds.length,
          formats,
          escalation_paths: escalations,
          intensity,
          status: "failed",
        } as any,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // ---------- Render ----------

  return (
    <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2 flex-wrap">
          <Radar className="w-5 h-5 text-cyan" />
          Generate Intelligence Batch
          <Badge
            variant="outline"
            className="ml-1 text-[10px] uppercase tracking-wider border-amber-400/40 text-amber-400 font-mono"
          >
            Signal Expansion
          </Badge>
          {isGenerating && (
            <span className="ml-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-mono text-cyan/80">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan animate-pulse" />
              Pipeline active
            </span>
          )}
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* ---------- SECTION 1 : CONTROLS ---------- */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Scenario Count */}
          <div className="space-y-2 rounded-md border border-glass-border bg-charcoal-deep/60 p-3">
            <div className="flex items-center justify-between">
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
                Scenario Count
              </label>
              <span className="text-xs font-mono text-cyan tabular-nums">
                {scenarioCount} scenarios
              </span>
            </div>
            <Slider
              min={5}
              max={100}
              step={1}
              value={[scenarioCount]}
              onValueChange={(v) => setScenarioCount(v[0])}
              disabled={isGenerating}
            />
          </div>

          {/* Behavioral Intensity */}
          <div className="space-y-2 rounded-md border border-glass-border bg-charcoal-deep/60 p-3">
            <div className="flex items-center justify-between">
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
                Behavioral Intensity
              </label>
              <span
                className={`text-xs font-mono tabular-nums uppercase tracking-wider ${
                  intensity === "High"
                    ? "text-amber-400"
                    : intensity === "Medium"
                      ? "text-cyan"
                      : "text-muted-foreground"
                }`}
              >
                {intensity}
              </span>
            </div>
            <Slider
              min={0}
              max={2}
              step={1}
              value={[intensityIdx]}
              onValueChange={(v) => setIntensityIdx(v[0])}
              disabled={isGenerating}
            />
            <div className="flex justify-between text-[9px] uppercase tracking-wider text-muted-foreground font-mono pt-1">
              <span>Low</span><span>Medium</span><span>High</span>
            </div>
          </div>
        </div>

        {/* Formats */}
        <div className="space-y-2">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
            Formats
          </label>
          <div className="flex flex-wrap gap-2">
            {FORMATS.map((f) => {
              const active = selectedFormats.has(f.key);
              return (
                <button
                  key={f.key}
                  type="button"
                  disabled={isGenerating}
                  onClick={() => toggleFormat(f.key)}
                  className={`px-3 py-1.5 rounded-sm border text-[11px] font-mono uppercase tracking-wider transition-colors ${
                    active
                      ? "border-cyan/60 bg-cyan/10 text-cyan"
                      : "border-glass-border bg-charcoal-deep text-muted-foreground hover:border-cyan/30"
                  } ${isGenerating ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Escalation Paths */}
        <div className="space-y-2">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
            Escalation Paths
          </label>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {ESCALATIONS.map((e) => (
              <label
                key={e.key}
                className={`flex items-center gap-2 ${
                  isGenerating ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
                }`}
              >
                <Checkbox
                  checked={selectedEscalations.has(e.key)}
                  disabled={isGenerating}
                  onCheckedChange={() => toggleEscalation(e.key)}
                  className="data-[state=checked]:bg-cyan data-[state=checked]:border-cyan"
                />
                <span className="text-[11px] font-mono uppercase tracking-wider text-foreground">
                  {e.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Archetype Focus */}
        <div className="space-y-2 rounded-md border border-glass-border bg-charcoal-deep/40 p-3">
          <div className="flex items-center justify-between">
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
              Archetype Focus
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={allArchetypes}
                disabled={isGenerating}
                onCheckedChange={(v) => setAllArchetypes(Boolean(v))}
                className="data-[state=checked]:bg-amber-400 data-[state=checked]:border-amber-400"
              />
              <span className="text-[10px] uppercase tracking-wider font-mono text-amber-400">
                All Archetypes
              </span>
            </label>
          </div>
          {!allArchetypes && (
            <div className="flex flex-wrap gap-2 pt-1">
              {ARCHETYPE_KEYS.map((k) => {
                const active = selectedArchetypes.has(k);
                return (
                  <button
                    key={k}
                    type="button"
                    disabled={isGenerating}
                    onClick={() => toggleArchetype(k)}
                    className={`px-2.5 py-1 rounded-sm border text-[10px] font-mono uppercase tracking-wider transition-colors ${
                      active
                        ? "border-cyan/60 bg-cyan/10 text-cyan"
                        : "border-glass-border bg-charcoal-deep text-muted-foreground hover:border-cyan/30"
                    } ${isGenerating ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    {ARCHETYPES[k].label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ---------- SECTION 2 : PROJECTION ---------- */}
        <div className="rounded-md border border-cyan/20 bg-cyan/[0.03] p-4">
          <div className="flex items-center gap-2 mb-3">
            <Crosshair className="w-3.5 h-3.5 text-cyan" />
            <span className="text-[10px] uppercase tracking-wider font-mono text-cyan">
              Intelligence Projection
            </span>
            <span className="ml-auto inline-flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-wider text-muted-foreground">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan/70 animate-pulse" />
              live
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <ProjectionStat label="Projected Assets" value={projectedAssets.toString()} accent="cyan" />
            <ProjectionStat label="Render Load" value={renderLoad} accent={renderLoad === "Saturating" || renderLoad === "Heavy" ? "amber" : "cyan"} />
            <ProjectionStat label="Est. Generation" value={`${(estimatedMs / 1000).toFixed(1)}s`} accent="muted" />
            <ProjectionStat label="Behavioral Coverage" value={coverage} accent={coverage === "Narrow" ? "amber" : "cyan"} />
          </div>
          <div className="mt-3 text-[10px] font-mono text-muted-foreground tracking-wider">
            {scenarioCount} scenarios × {selectedFormats.size} formats × {selectedEscalations.size} escalations
            {" · "}intensity {intensity.toLowerCase()}
            {" · "}{archetypePool.length} archetype{archetypePool.length === 1 ? "" : "s"}
          </div>
        </div>

        {/* ---------- SECTION 3 : CTA ---------- */}
        <div className="space-y-3">
          <Button
            onClick={handleGenerate}
            disabled={isGenerating}
            className={`w-full h-12 bg-cyan/15 hover:bg-cyan/25 text-cyan border border-cyan/50 font-mono uppercase tracking-[0.2em] text-sm ${
              isGenerating ? "animate-pulse" : ""
            }`}
          >
            {isGenerating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Layers className="w-4 h-4 mr-2" />
            )}
            {isGenerating ? "Indexing batch…" : "Generate Intelligence Batch"}
          </Button>

          {lastError && !isGenerating && (
            <div className="text-[11px] font-mono text-red-400 inline-flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" />
              Pipeline interrupted — {lastError}
            </div>
          )}
          {lastBatch && !isGenerating && (
            <div className="text-[11px] font-mono text-cyan/80 uppercase tracking-wider">
              Batch indexed · {lastBatch.count} asset{lastBatch.count === 1 ? "" : "s"} · {lastBatch.ms}ms · queues updated
            </div>
          )}
        </div>

        {/* Live ops feed */}
        {(isGenerating || pipelineLog.length > 0) && (
          <div className="rounded-md border border-glass-border bg-charcoal-deep/70 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Activity className="w-3.5 h-3.5 text-cyan" />
              <span className="text-[10px] uppercase tracking-wider font-mono text-cyan">
                Forensic Pipeline State
              </span>
              {isGenerating && (
                <span className="ml-auto text-[10px] font-mono tabular-nums text-amber-400">
                  {liveCounter} indexed
                </span>
              )}
            </div>
            {isGenerating && pipelineStage && (
              <div className="text-[11px] font-mono text-cyan/90">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan animate-pulse mr-2 align-middle" />
                {pipelineStage}
              </div>
            )}
            <div className="max-h-[140px] overflow-y-auto space-y-0.5 font-mono text-[10px] text-muted-foreground">
              {pipelineLog.length === 0 ? (
                <div>Awaiting first event…</div>
              ) : (
                pipelineLog.map((line, i) => (
                  <div key={i} className="leading-relaxed">{line}</div>
                ))
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------- Projection stat ----------

function ProjectionStat({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent: "cyan" | "amber" | "muted";
}) {
  const valueColor =
    accent === "cyan" ? "text-cyan" :
    accent === "amber" ? "text-amber-400" : "text-foreground";
  const dotColor =
    accent === "cyan" ? "bg-cyan" :
    accent === "amber" ? "bg-amber-400" : "bg-muted-foreground";
  return (
    <div className="rounded-sm border border-glass-border bg-charcoal-deep/70 px-3 py-2">
      <div className="flex items-center gap-1.5 mb-1">
        <span className={`w-1.5 h-1.5 rounded-full ${dotColor} ${accent !== "muted" ? "animate-pulse" : ""}`} />
        <span className="text-[9px] uppercase tracking-wider font-mono text-muted-foreground">
          {label}
        </span>
      </div>
      <div className={`text-base font-mono tabular-nums ${valueColor}`}>{value}</div>
    </div>
  );
}
